import { callFunction, callFunctionDelete } from "./client";
import type { ElectedRole, MembershipCategory, MemberStatus } from "./members";

export type AdministrativeRole = "admin_tecnico" | "secretaria_daf" | "marketing";

export interface CreateStaffAccountInput {
  name: string;
  email: string;
}

export interface CreateStaffAccountResponse {
  username: string;
  temporary_password: string;
  requires_password_change: true;
}

export interface InstitutionalRoleInput {
  member_id: string;
  role: ElectedRole;
  decision_reference: string;
}

export interface InstitutionalRoleResponse {
  status: "granted" | "revoked";
  grant_id: string;
}

export interface AdministrativeRoleInput {
  member_id?: string;
  username?: string;
  role: AdministrativeRole;
  decision_reference?: string | null;
}

export type AdministrativeRoleResponse = InstitutionalRoleResponse;

export type AccessTargetInput =
  | { member_id: string; username?: never }
  | { username: string; member_id?: never };

export interface AccessTargetSearchResult {
  kind: "member" | "technical";
  member_id: string | null;
  login_identity_id: string | null;
  display_name: string;
  username: string | null;
  membership_category: MembershipCategory | null;
  status: MemberStatus | null;
}

export interface AccessTargetEligibility {
  eligible: boolean;
  reasons: string[];
}

export interface MemberAccessTarget {
  kind: "member";
  member_id: string;
  display_name: string;
  username: string | null;
  membership_category: MembershipCategory;
  status: MemberStatus;
  active_roles: string[];
  elected_eligibility: AccessTargetEligibility;
}

export interface TechnicalAccessTarget {
  kind: "technical";
  member_id: null;
  display_name: string;
  username: string;
  membership_category: null;
  status: null;
  active_roles: string[];
  elected_eligibility: AccessTargetEligibility;
}

export type AccessTarget = MemberAccessTarget | TechnicalAccessTarget;

export function createStaffAccount(
  accessToken: string,
  input: CreateStaffAccountInput,
): Promise<CreateStaffAccountResponse> {
  return callFunction<CreateStaffAccountInput, CreateStaffAccountResponse>("staff-create", input, { accessToken });
}

export function readAccessTarget(
  accessToken: string,
  input: AccessTargetInput,
): Promise<AccessTarget> {
  return callFunction<AccessTargetInput, AccessTarget>("access-target-read", input, { accessToken });
}

export function searchAccessTargets(
  accessToken: string,
  query: string,
  memberOnly = false,
): Promise<AccessTargetSearchResult[]> {
  return callFunction<{ query: string; member_only: boolean }, AccessTargetSearchResult[]>("access-target-read", {
    query,
    member_only: memberOnly,
  }, { accessToken });
}

export function grantInstitutionalRole(
  accessToken: string,
  input: InstitutionalRoleInput,
): Promise<InstitutionalRoleResponse> {
  return callFunction<InstitutionalRoleInput, InstitutionalRoleResponse>("institutional-role-grant", input, { accessToken });
}

export function revokeInstitutionalRole(
  accessToken: string,
  input: InstitutionalRoleInput,
): Promise<InstitutionalRoleResponse> {
  return callFunctionDelete<InstitutionalRoleInput, InstitutionalRoleResponse>("institutional-role-grant", input, { accessToken });
}

export function grantAdministrativeRole(
  accessToken: string,
  input: AdministrativeRoleInput,
): Promise<AdministrativeRoleResponse> {
  return callFunction<AdministrativeRoleInput, AdministrativeRoleResponse>("administrative-role-grant", input, { accessToken });
}

export function revokeAdministrativeRole(
  accessToken: string,
  input: AdministrativeRoleInput,
): Promise<AdministrativeRoleResponse> {
  return callFunctionDelete<AdministrativeRoleInput, AdministrativeRoleResponse>("administrative-role-grant", input, { accessToken });
}
